import { z } from 'zod/v4';

export {
  RICH_CONTENT_ALLOWED_ATTRIBUTE_NAMES,
  RICH_CONTENT_ALLOWED_ATTRIBUTES,
  RICH_CONTENT_ALLOWED_TAGS,
  RICH_CONTENT_FORBIDDEN_TAGS,
  RICH_CONTENT_IFRAME_FORCED_ATTRIBUTES,
  RICH_CONTENT_SAFE_CSS_PROPERTIES,
  filterRichContentStyle,
  isAllowedRichContentIframeSrc,
  sanitizeRichContentId
} from './rich-content-policy';

export const MICROSITE_SCHEMA_VERSION = 2 as const;
export const MICROSITE_MAX_COMPONENTS = 50;
export const MICROSITE_MAX_RICH_CONTENT_CHARACTERS = 20_000;

const identifierSchema = z.uuid();
const componentIdSchema = identifierSchema;

const headerComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('header'),
  props: z.strictObject({ showLogo: z.boolean(), showSiteName: z.boolean() })
});

const heroComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('hero'),
  props: z.strictObject({
    showImage: z.boolean(),
    showPromptChoices: z.boolean(),
    eyebrow: z.string().max(120).optional()
  })
});

const approvedContentSectionsComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('approvedContentSections'),
  props: z.strictObject({
    columns: z.union([z.literal(1), z.literal(2)]),
    showHighlights: z.boolean(),
    showCitations: z.boolean()
  })
});

const sourcesFooterComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('sourcesFooter'),
  props: z.strictObject({ heading: z.string().trim().min(1).max(80) })
});

const textComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('text'),
  props: z.strictObject({
    heading: z.string().max(160).optional(),
    text: z.string().min(1).max(4_000),
    alignment: z.enum(['left', 'center', 'right'])
  })
});

const imageComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('image'),
  props: z.strictObject({
    assetId: identifierSchema,
    alt: z.string().max(300),
    caption: z.string().max(300).optional(),
    fit: z.enum(['cover', 'contain'])
  })
});

const buttonComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('button'),
  props: z.strictObject({
    label: z.string().trim().min(1).max(80),
    href: z.string().trim().min(1).max(2_048),
    appearance: z.enum(['primary', 'outline', 'link']),
    alignment: z.enum(['left', 'center', 'right']),
    openInNewTab: z.boolean()
  })
});

const spacerComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('spacer'),
  props: z.strictObject({ size: z.enum(['small', 'medium', 'large']) })
});

const dividerComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('divider'),
  props: z.strictObject({ style: z.enum(['solid', 'dashed']) })
});

export const micrositeRichContentEmbedSchema = z.strictObject({
  provider: z.enum(['youtube', 'vimeo', 'google-maps']),
  url: z.url().max(2_048),
  title: z.string().trim().min(1).max(120)
});

export const micrositeRichContentComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('richContent'),
  props: z.strictObject({
    html: z.string().max(MICROSITE_MAX_RICH_CONTENT_CHARACTERS),
    embeds: z.array(micrositeRichContentEmbedSchema).max(6)
  })
});

const contactFormFieldSchema = z.strictObject({
  type: z.enum(['name', 'email', 'phone', 'account', 'message']),
  label: z.string().trim().min(1).max(80),
  enabled: z.boolean(),
  required: z.boolean()
});

const contactFormComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('contactForm'),
  props: z.strictObject({
    heading: z.string().trim().min(1).max(140),
    supportingText: z.string().max(400).optional(),
    submitLabel: z.string().trim().min(1).max(60),
    successMessage: z.string().trim().min(1).max(400),
    fields: z.array(contactFormFieldSchema).min(1).max(5)
  }).superRefine((value, context) => {
    const types = value.fields.map(({ type }) => type);
    if (new Set(types).size !== types.length) {
      context.addIssue({ code: 'custom', path: ['fields'], message: 'Contact form fields must be unique.' });
    }
  })
});

const aiChatComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('aiChat'),
  props: z.strictObject({
    heading: z.string().trim().min(1).max(140),
    welcomeMessage: z.string().trim().min(1).max(600),
    inputPlaceholder: z.string().trim().min(1).max(160),
    submitLabel: z.string().trim().min(1).max(60),
    handoffEnabled: z.boolean(),
    handoffLabel: z.string().trim().min(1).max(80),
    suggestedQuestions: z.array(z.string().trim().min(1).max(160)).max(6).default([]),
    displayMode: z.enum(['inline', 'floating']).default('inline')
  })
});

const clickToCallComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('clickToCall'),
  props: z.strictObject({
    heading: z.string().trim().min(1).max(140),
    supportingText: z.string().max(400).optional(),
    browserVoice: z.boolean(),
    callbackRequest: z.boolean(),
    tapToDial: z.boolean(),
    browserVoiceLabel: z.string().trim().min(1).max(80),
    callbackRequestLabel: z.string().trim().min(1).max(80),
    tapToDialLabel: z.string().trim().min(1).max(80)
  })
});

const voiceAgentComponentSchema = z.strictObject({
  id: componentIdSchema,
  type: z.literal('voiceAgent'),
  props: z.strictObject({
    heading: z.string().trim().min(1).max(140),
    supportingText: z.string().max(400).optional(),
    startLabel: z.string().trim().min(1).max(80)
  })
});

export const micrositeLayoutComponentSchema = z.discriminatedUnion('type', [
  headerComponentSchema,
  heroComponentSchema,
  approvedContentSectionsComponentSchema,
  sourcesFooterComponentSchema,
  textComponentSchema,
  imageComponentSchema,
  buttonComponentSchema,
  spacerComponentSchema,
  dividerComponentSchema,
  micrositeRichContentComponentSchema,
  contactFormComponentSchema,
  aiChatComponentSchema,
  clickToCallComponentSchema,
  voiceAgentComponentSchema
]);

const micrositeLayoutColumnSchema = z.strictObject({
  id: identifierSchema,
  width: z.enum(['full', 'half', 'third', 'two-thirds']),
  components: z.array(micrositeLayoutComponentSchema).max(MICROSITE_MAX_COMPONENTS)
});

const micrositeLayoutRowSchema = z.strictObject({
  id: identifierSchema,
  columns: z.array(micrositeLayoutColumnSchema).min(1).max(4)
});

export const micrositeLayoutSchema = z.strictObject({
  rows: z.array(micrositeLayoutRowSchema).min(1).max(50)
}).superRefine((layout, context) => {
  const rowIds = new Set<string>();
  const columnIds = new Set<string>();
  const componentIds = new Set<string>();
  let componentCount = 0;
  layout.rows.forEach((row, rowIndex) => {
    if (rowIds.has(row.id)) {
      context.addIssue({ code: 'custom', path: ['rows', rowIndex, 'id'], message: 'Layout row identifiers must be unique.' });
    }
    rowIds.add(row.id);
    row.columns.forEach((column, columnIndex) => {
      if (columnIds.has(column.id)) {
        context.addIssue({ code: 'custom', path: ['rows', rowIndex, 'columns', columnIndex, 'id'], message: 'Layout column identifiers must be unique.' });
      }
      columnIds.add(column.id);
      column.components.forEach((component, componentIndex) => {
        componentCount += 1;
        if (componentIds.has(component.id)) {
          context.addIssue({ code: 'custom', path: ['rows', rowIndex, 'columns', columnIndex, 'components', componentIndex, 'id'], message: 'Component identifiers must be unique.' });
        }
        componentIds.add(component.id);
      });
    });
  });
  if (componentCount > MICROSITE_MAX_COMPONENTS) {
    context.addIssue({ code: 'custom', path: ['rows'], message: `A microsite may contain at most ${MICROSITE_MAX_COMPONENTS} components.` });
  }
});

export const MICROSITE_VISUAL_BRAND_DEFAULTS = {
  accentColor: '#111111',
  surfaceColor: '#ffffff',
  mutedColor: '#f5f5f5',
  headingFont: 'system-sans',
  bodyFont: 'system-sans',
  typeScale: 'standard',
  spacing: 'standard',
  contentWidth: 'standard',
  logoTreatment: 'natural',
  logoScale: 'medium',
  imageryTreatment: 'natural',
  imageShape: 'rounded',
  buttonStyle: 'solid',
  buttonShape: 'rounded',
  buttonWeight: 'medium',
  surfaceStyle: 'bordered',
  borderStyle: 'subtle',
  radius: 'medium',
  responsiveLayout: 'adaptive',
  heroAlignment: 'left'
} as const;

export const MICROSITE_VISUAL_BRAND_KEYS = Object.keys(MICROSITE_VISUAL_BRAND_DEFAULTS) as Array<keyof typeof MICROSITE_VISUAL_BRAND_DEFAULTS>;

export const MICROSITE_FONT_STACKS = {
  'system-sans': 'ui-sans-serif, system-ui, sans-serif',
  'humanist-sans': '"Trebuchet MS", "Segoe UI", sans-serif',
  'editorial-serif': 'Georgia, "Times New Roman", serif',
  monospace: 'ui-monospace, "Cascadia Code", "Courier New", monospace'
} as const;

const safeFontSchema = z.enum(['system-sans', 'humanist-sans', 'editorial-serif', 'monospace']);

export const micrositeBrandSchema = z.strictObject({
  logoAssetId: identifierSchema.nullish(),
  heroAssetId: identifierSchema.nullish(),
  primaryColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  backgroundColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  foregroundColor: z.string().regex(/^#[0-9a-f]{6}$/i),
  accentColor: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  surfaceColor: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  mutedColor: z.string().regex(/^#[0-9a-f]{6}$/i).optional(),
  headingFont: safeFontSchema.optional(),
  bodyFont: safeFontSchema.optional(),
  typeScale: z.enum(['compact', 'standard', 'large']).optional(),
  spacing: z.enum(['compact', 'standard', 'generous']).optional(),
  contentWidth: z.enum(['narrow', 'standard', 'wide']).optional(),
  logoTreatment: z.enum(['natural', 'contained', 'monochrome']).optional(),
  logoScale: z.enum(['small', 'medium', 'large']).optional(),
  imageryTreatment: z.enum(['natural', 'soft', 'high-contrast', 'monochrome']).optional(),
  imageShape: z.enum(['square', 'rounded', 'pill']).optional(),
  buttonStyle: z.enum(['solid', 'soft', 'outline']).optional(),
  buttonShape: z.enum(['square', 'rounded', 'pill']).optional(),
  buttonWeight: z.enum(['regular', 'medium', 'bold']).optional(),
  surfaceStyle: z.enum(['flat', 'bordered', 'elevated']).optional(),
  borderStyle: z.enum(['none', 'subtle', 'strong']).optional(),
  radius: z.enum(['none', 'small', 'medium', 'large']).optional(),
  responsiveLayout: z.enum(['adaptive', 'stacked']).optional(),
  heroAlignment: z.enum(['left', 'center']).optional()
});

const promptChoiceSchema = z.strictObject({
  id: identifierSchema,
  label: z.string().trim().min(1).max(80),
  instruction: z.string().trim().min(1).max(2_000)
});

const approvedContentSchema = z.strictObject({
  sourceId: identifierSchema,
  sourceType: z.enum(['website', 'document', 'manual']),
  version: z.number().int().positive(),
  label: z.string().trim().min(1).max(160)
});

const contactSchema = z.strictObject({
  email: z.email().max(254).optional(),
  phone: z.string().max(40).optional(),
  address: z.string().max(300).optional()
});

const enquiryCallToActionSchema = z.strictObject({
  heading: z.string().trim().min(1).max(140),
  label: z.string().trim().min(1).max(60),
  supportingText: z.string().max(400).optional()
});

const configurationFields = {
  headline: z.string().trim().min(1).max(160),
  description: z.string().trim().min(1).max(4_000),
  publicWebsiteUrl: z.url().max(2_048),
  brand: micrositeBrandSchema,
  startingInstruction: z.string().trim().min(1).max(4_000),
  promptChoices: z.array(promptChoiceSchema).min(1).max(12),
  approvedContent: z.array(approvedContentSchema).max(100),
  contact: contactSchema,
  enquiryCallToAction: enquiryCallToActionSchema
};

export const micrositeConfigurationV1Schema = z.strictObject({
  schemaVersion: z.literal(1),
  ...configurationFields
});

export const micrositeConfigurationDocumentSchema = z.strictObject({
  schemaVersion: z.literal(MICROSITE_SCHEMA_VERSION),
  ...configurationFields,
  layout: micrositeLayoutSchema
});

export type MicrositeConfigurationV1 = z.infer<typeof micrositeConfigurationV1Schema>;
export type MicrositeConfigurationDocument = z.infer<typeof micrositeConfigurationDocumentSchema>;
export type MicrositeBrand = z.infer<typeof micrositeBrandSchema>;
export type MicrositeLayout = z.infer<typeof micrositeLayoutSchema>;
export type MicrositeLayoutComponent = z.infer<typeof micrositeLayoutComponentSchema>;
export type MicrositeRichContentEmbed = z.infer<typeof micrositeRichContentEmbedSchema>;

export function resolveMicrositeBrand(brand: MicrositeBrand) {
  return {
    ...MICROSITE_VISUAL_BRAND_DEFAULTS,
    ...brand,
    accentColor: brand.accentColor ?? brand.primaryColor,
    surfaceColor: brand.surfaceColor ?? brand.backgroundColor,
    mutedColor: brand.mutedColor ?? brand.backgroundColor
  };
}

export function hasMicrositeVisualBrand(brand: MicrositeBrand): boolean {
  return MICROSITE_VISUAL_BRAND_KEYS.some((key) => brand[key] !== undefined);
}

export type ResolvedMicrositeBrand = ReturnType<typeof resolveMicrositeBrand>;

const DEFAULT_IDS = {
  rows: [
    '00000000-0000-4000-8000-000000000101',
    '00000000-0000-4000-8000-000000000102',
    '00000000-0000-4000-8000-000000000103',
    '00000000-0000-4000-8000-000000000104',
    '00000000-0000-4000-8000-000000000105'
  ],
  columns: [
    '00000000-0000-4000-8000-000000000201',
    '00000000-0000-4000-8000-000000000202',
    '00000000-0000-4000-8000-000000000203',
    '00000000-0000-4000-8000-000000000204',
    '00000000-0000-4000-8000-000000000205'
  ],
  components: [
    '00000000-0000-4000-8000-000000000301',
    '00000000-0000-4000-8000-000000000302',
    '00000000-0000-4000-8000-000000000303',
    '00000000-0000-4000-8000-000000000304',
    '00000000-0000-4000-8000-000000000305'
  ]
} as const;

export function createDefaultLayout(
  enquiryCallToAction: MicrositeConfigurationV1['enquiryCallToAction']
): MicrositeLayout {
  const components: MicrositeLayoutComponent[] = [
    { id: DEFAULT_IDS.components[0], type: 'header', props: { showLogo: true, showSiteName: true } },
    { id: DEFAULT_IDS.components[1], type: 'hero', props: { showImage: true, showPromptChoices: true } },
    { id: DEFAULT_IDS.components[2], type: 'approvedContentSections', props: { columns: 2, showHighlights: true, showCitations: true } },
    {
      id: DEFAULT_IDS.components[3],
      type: 'contactForm',
      props: {
        heading: enquiryCallToAction.heading,
        supportingText: enquiryCallToAction.supportingText,
        submitLabel: enquiryCallToAction.label,
        successMessage: "Thanks — your enquiry has been received. We'll be in touch shortly.",
        fields: [
          { type: 'name', label: 'Name', enabled: true, required: true },
          { type: 'email', label: 'Email', enabled: true, required: true },
          { type: 'phone', label: 'Phone', enabled: true, required: false },
          { type: 'account', label: 'Organisation', enabled: true, required: false },
          { type: 'message', label: 'How can we help?', enabled: true, required: true }
        ]
      }
    },
    { id: DEFAULT_IDS.components[4], type: 'sourcesFooter', props: { heading: 'Approved sources' } }
  ];
  return {
    rows: components.map((component, index) => ({
      id: DEFAULT_IDS.rows[index]!,
      columns: [{ id: DEFAULT_IDS.columns[index]!, width: 'full', components: [component] }]
    }))
  };
}

export function upgradeMicrositeDocument(input: unknown): MicrositeConfigurationDocument {
  const current = micrositeConfigurationDocumentSchema.safeParse(input);
  if (current.success) return current.data;
  const legacy = micrositeConfigurationV1Schema.parse(input);
  return micrositeConfigurationDocumentSchema.parse({
    ...legacy,
    schemaVersion: MICROSITE_SCHEMA_VERSION,
    layout: createDefaultLayout(legacy.enquiryCallToAction)
  });
}
